/**
 * Step lists and the predicates that decide which of them apply to the
 * current formData. The renderer walks a flat list of string keys; steps that
 * do not apply are skipped during navigation rather than removed from the
 * list, so indexes stay stable while the donor fills the form.
 *
 * Donation: amount → [upsell] → donorInfo → payment → [addressToBoleto] → chargeResult → [addressAfterCharge] → confirmation
 * Lead:     donorInfo → [address] → confirmation
 */

// Steps reached only after the donation was sent to the API.
export const SUBMITTED_STEPS = ['chargeResult', 'addressAfterCharge', 'confirmation'];

export function buildSteps(config) {
  const { Goal, Address } = config.Behaviour;
  const hasAddress = Address?.Enabled;

  if (Goal === 'Lead') {
    return [
      'donorInfo',
      ...(hasAddress ? ['address'] : []),
      'confirmation'
    ];
  }

  // Donation — upsell is injected dynamically after amount selection,
  // so we include the slot here; AcquisitionForm handles skipping it when not applicable.
  return [
    'amount',
    'upsell',
    'donorInfo',
    'payment',
    'addressToBoleto',
    'chargeResult',
    'addressAfterCharge',
    'confirmation'
  ];
}

export function resolveDefaultFrequency(config) {
  const { Behaviour } = config;

  return Behaviour.Frequencies?.find(f => f.Id === Behaviour.DefaultFrequency)
    || Behaviour.Frequencies?.[0]
    || null;
}

export function buildInitialFormData(config, overrides = null) {
  const defaultFreq = resolveDefaultFrequency(config);

  return {
    selection: [],
    frequencyId: defaultFreq?.Id || null,
    amount: defaultFreq?.SuggestedAmounts?.[0] || 0,
    isCustomAmount: false,
    customAmount: '',
    donorInfo: {},
    address: {},
    paymentMethod: defaultFreq?.PaymentMethods?.[0] || null,
    upsellAccepted: null,
    processingCostAccepted: false,
    paymentDetails: {},
    customFields: null,
    customFieldValues: {},
    submissionId: null,
    chargeResult: null,
    apiError: null,
    ...overrides
  };
}

/**
 * Determines whether the upsell step should be shown for the current formData.
 */
export function shouldShowUpsell(config, formData) {
  const { Upsell, Frequencies } = config.Behaviour;
  if (!Upsell?.Enabled) return false;
  if (formData.frequencyId !== 'OneTime') return false;
  return Frequencies.some(f => f.Id === 'Monthly');
}

/**
 * Pre-charge address capture, mandatory: Boleto requires a payer address to
 * register the charge; the other payment methods submit straight through.
 */
export function shouldShowAddressToBoleto(formData) {
  return formData.paymentMethod === 'Boleto';
}

/**
 * Optional post-payment address capture: for donation methods that don't require
 * an address up front (everything except Boleto), ask the donor to add it after
 * the payment is submitted — but only if the acquisition form collects addresses at all.
 */
export function shouldShowAddressAfterCharge(config, formData) {
  return config.Behaviour.Goal !== 'Lead'
    && Boolean(config.Behaviour.Address?.Enabled)
    && formData.paymentMethod !== 'Boleto';
}
